import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Plus, Trash2, Copy, ChevronDown } from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api/dd/organico`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "mai");

const THEMES = {
  light: { box: "border-gray-200 bg-gray-50", text: "text-[#16345c]", muted: "text-gray-500", input: "border-gray-300 bg-white text-gray-800", row: "border-gray-200 bg-white", btn: "bg-[#16345c] hover:bg-[#0d1b2f] text-white" },
  dark: { box: "border-white/10 bg-white/5", text: "text-white", muted: "text-gray-400", input: "border-white/10 bg-white/5 text-white", row: "border-white/10 bg-white/5", btn: "bg-[#ff7e00] hover:bg-[#e86f00] text-white" },
};

const NewKeyBox = ({ created, t, onClose }) => {
  const copy = () => navigator.clipboard?.writeText(created.key).then(() => toast.success("Chiave copiata"));
  return (
    <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3" data-testid="dd-new-key-box">
      <p className="text-[12px] font-bold text-emerald-800 mb-1">Copia ora la chiave: non sarà più visibile.</p>
      <div className="flex items-center gap-2">
        <code className="flex-1 min-w-0 break-all text-[12px] bg-white border border-emerald-200 rounded px-2 py-1 text-gray-800" data-testid="dd-new-key-value">{created.key}</code>
        <button type="button" onClick={copy} data-testid="dd-new-key-copy" className="shrink-0 p-1.5 rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-100"><Copy size={14} /></button>
      </div>
      <button type="button" onClick={onClose} className="mt-2 text-[12px] font-semibold text-emerald-700 underline">Ho copiato la chiave</button>
    </div>
  );
};

// Chiavi API per azienda per GET /api/dd/organico (header X-API-Key): generazione (mostrata una volta) e revoca.
export const DdApiKeysPanel = ({ client, keysPath, company, dark = false, defaultOpen = false }) => {
  const t = THEMES[dark ? "dark" : "light"];
  const [open, setOpen] = useState(defaultOpen);
  const [keys, setKeys] = useState([]);
  const [label, setLabel] = useState("");
  const [created, setCreated] = useState(null);

  const load = () => client.get(keysPath).then((r) => setKeys(r.data.keys || [])).catch(() => setKeys([]));
  useEffect(() => { if (open) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [open, keysPath]);

  const create = async () => {
    try {
      const r = await client.post(keysPath, { label });
      setCreated(r.data); setLabel(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Generazione non riuscita"); }
  };
  const revoke = async (k) => {
    if (!window.confirm(`Revocare la chiave "${k.label}"? Le integrazioni che la usano smetteranno di funzionare.`)) return;
    try { await client.delete(`${keysPath}/${k.id}`); toast.success("Chiave revocata"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Revoca non riuscita"); }
  };

  return (
    <div className={`mt-4 rounded-md border ${t.box}`} data-testid="dd-api-keys-panel">
      <button type="button" onClick={() => setOpen((v) => !v)} data-testid="dd-api-keys-toggle" className={`w-full flex items-center justify-between px-3 py-2.5 text-[13px] font-bold ${t.text}`}>
        <span className="flex items-center gap-2"><KeyRound size={15} /> Accesso API (X-API-Key)</span>
        <ChevronDown size={15} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-3">
          <p className={`text-[12px] ${t.muted}`}>
            <span className="font-mono break-all">GET {API_URL}?azienda_id={company || "<codice>"}&data=aaaa-mm-gg</span> con header <span className="font-mono">X-API-Key</span>: restituisce lo stesso CSV.
          </p>
          {created && <NewKeyBox created={created} t={t} onClose={() => setCreated(null)} />}
          <div className="flex items-center gap-2">
            <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Etichetta (es. App due diligence)" data-testid="dd-api-key-label"
              className={`flex-1 min-w-0 border rounded-md px-3 py-1.5 text-[13px] outline-none ${t.input}`} />
            <button type="button" onClick={create} data-testid="dd-api-key-create" className={`flex items-center gap-1.5 text-[12.5px] font-bold px-3 py-1.5 rounded-md ${t.btn}`}><Plus size={14} /> Genera chiave</button>
          </div>
          <div className="space-y-1.5" data-testid="dd-api-keys-list">
            {keys.length === 0 && <p className={`text-[12px] ${t.muted}`}>Nessuna chiave attiva.</p>}
            {keys.map((k) => (
              <div key={k.id} className={`flex items-center gap-3 border rounded-md px-3 py-2 ${t.row}`} data-testid={`dd-api-key-${k.id}`}>
                <div className="flex-1 min-w-0">
                  <div className={`text-[12.5px] font-bold ${t.text}`}>{k.label} <span className={`font-mono font-normal ${t.muted}`}>{k.prefix}…</span></div>
                  <div className={`text-[11px] ${t.muted}`}>Creata {fmt(k.created_at)} · ultimo uso {fmt(k.last_used_at)}</div>
                </div>
                <button type="button" onClick={() => revoke(k)} data-testid={`dd-api-key-revoke-${k.id}`} title="Revoca" className="p-1.5 rounded text-red-500 hover:bg-red-500/10"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DdApiKeysPanel;
